import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNavbar from '../../components/Navigation/PublicNavbar';
import { 
  Check, 
  X, 
  Star, 
  Crown, 
  Users, 
  BarChart3, 
  Brain, 
  Video, 
  Shield,
  ArrowRight
} from 'lucide-react';

type BillingCycle = 'monthly' | 'yearly';

interface PricingFeature {
  name: string;
  included: boolean;
  highlight?: boolean;
}

interface PricingPlan {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  monthlyPrice: number;
  yearlyPrice: number;
  popular?: boolean;
  enterprise?: boolean;
  features: PricingFeature[];
  cta: string;
  gradient: string;
}

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  const plans: PricingPlan[] = [
    {
      id: 'starter',
      name: 'Starter',
      description: 'Perfecto para clubes pequeños y academias',
      icon: Users,
      monthlyPrice: 0,
      yearlyPrice: 0,
      features: [
        { name: 'Hasta 50 jugadores', included: true },
        { name: 'Búsqueda básica de jugadores', included: true },
        { name: 'Informes básicos', included: true },
        { name: 'Dashboard principal', included: true },
        { name: 'Soporte por email', included: true },
        { name: 'Análisis IA avanzado', included: false },
        { name: 'Análisis de video', included: false },
        { name: 'Machine Learning', included: false },
        { name: 'API access', included: false },
        { name: 'Soporte prioritario', included: false }
      ],
      cta: 'Comenzar Prueba',
      gradient: 'from-blue-600 to-blue-700'
    },
    {
      id: 'professional',
      name: 'Professional',
      description: 'Para clubes profesionales y scouts avanzados',
      icon: BarChart3,
      monthlyPrice: 299,
      yearlyPrice: 2990,
      popular: true,
      features: [
        { name: 'Hasta 500 jugadores', included: true },
        { name: 'Búsqueda IA conversacional', included: true, highlight: true },
        { name: 'Informes avanzados', included: true },
        { name: 'Dashboard completo', included: true },
        { name: 'Análisis IA avanzado', included: true, highlight: true },
        { name: 'Análisis básico de video', included: true },
        { name: 'Métricas físicas', included: true },
        { name: 'Soporte prioritario', included: true },
        { name: 'Machine Learning', included: false },
        { name: 'API access', included: false }
      ],
      cta: 'Más Popular',
      gradient: 'from-blue-600 to-indigo-700'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'Solución completa para organizaciones grandes',
      icon: Crown,
      monthlyPrice: 799,
      yearlyPrice: 7990,
      enterprise: true,
      features: [
        { name: 'Jugadores ilimitados', included: true },
        { name: 'Búsqueda IA conversacional', included: true },
        { name: 'Todos los informes', included: true },
        { name: 'Dashboard personalizable', included: true },
        { name: 'Análisis IA avanzado', included: true },
        { name: 'Análisis completo de video', included: true, highlight: true },
        { name: 'Machine Learning completo', included: true, highlight: true },
        { name: 'API access completo', included: true },
        { name: 'Soporte 24/7', included: true },
        { name: 'Onboarding personalizado', included: true, highlight: true }
      ],
      cta: 'Contactar Ventas',
      gradient: 'from-indigo-600 to-purple-700'
    }
  ];

  const getPrice = (plan: PricingPlan): number => {
    return billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
  };

  const getSavings = (plan: PricingPlan): number => {
    const monthlyTotal = plan.monthlyPrice * 12;
    const yearlyPrice = plan.yearlyPrice;
    return Math.round(((monthlyTotal - yearlyPrice) / monthlyTotal) * 100);
  };

  const handlePlanSelect = (planId: string): void => {
    if (planId === 'enterprise') {
      window.open('mailto:info@scoutmindai.com?subject=Enterprise Plan Inquiry', '_blank');
    } else {
      navigate('/register', { state: { selectedPlan: planId, billingCycle } });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700">
      <PublicNavbar />
      
      <div className="pt-20 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-7xl mx-auto text-center py-20">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-8">
            Planes y <span className="text-blue-300">Precios</span>
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed mb-8">
            Elige el plan perfecto para tu organización deportiva
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center space-x-4 mb-12">
            <span className={`text-lg font-medium ${billingCycle === 'monthly' ? 'text-white' : 'text-blue-200'}`}>
              Mensual
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className="relative inline-flex h-8 w-16 items-center rounded-full bg-white/20 backdrop-blur-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                  billingCycle === 'yearly' ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
            </button>
            <div className="flex items-center space-x-2">
              <span className={`text-lg font-medium ${billingCycle === 'yearly' ? 'text-white' : 'text-blue-200'}`}>
                Anual
              </span>
              {billingCycle === 'yearly' && (
                <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  Ahorra hasta 20%
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 pb-20">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const price = getPrice(plan);
            const savings = getSavings(plan);
            
            return (
              <div
                key={plan.id}
                className={`relative bg-white/10 backdrop-blur-md rounded-3xl p-8 border transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                  plan.popular 
                    ? 'border-blue-400 ring-2 ring-blue-400 ring-opacity-50' 
                    : 'border-white/20 hover:border-white/30'
                }`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-2 rounded-full text-sm font-bold flex items-center space-x-1">
                      <Star className="w-4 h-4" />
                      <span>Más Popular</span>
                    </div>
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-8">
                  <div className={`w-16 h-16 bg-gradient-to-br ${plan.gradient} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-blue-100 text-sm">{plan.description}</p>
                </div>

                {/* Pricing */}
                <div className="text-center mb-8">
                  <div className="flex items-baseline justify-center space-x-1">
                    <span className="text-4xl font-bold text-white">€{price}</span>
                    <span className="text-blue-200">/{billingCycle === 'monthly' ? 'mes' : 'año'}</span>
                  </div>
                  {billingCycle === 'yearly' && savings > 0 && (
                    <p className="text-green-400 text-sm mt-2 font-medium">
                      Ahorras {savings}% vs mensual
                    </p>
                  )}
                </div>

                {/* Features */}
                <div className="space-y-4 mb-8">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      {feature.included ? (
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                          feature.highlight ? 'bg-green-500' : 'bg-blue-500'
                        }`}>
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-gray-600 flex items-center justify-center">
                          <X className="w-3 h-3 text-gray-400" />
                        </div>
                      )}
                      <span className={`text-sm ${
                        feature.included 
                          ? feature.highlight 
                            ? 'text-white font-medium' 
                            : 'text-blue-100' 
                          : 'text-gray-400'
                      }`}>
                        {feature.name}
                      </span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => handlePlanSelect(plan.id)}
                  className={`w-full py-4 px-6 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 focus:ring-4 focus:ring-opacity-50 focus:outline-none flex items-center justify-center space-x-2 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white focus:ring-blue-500'
                      : plan.enterprise
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800 text-white focus:ring-indigo-500'
                      : 'bg-white/20 hover:bg-white/30 text-white focus:ring-white'
                  }`}
                >
                  <span>{plan.cta}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Features Comparison */}
        <div className="max-w-6xl mx-auto pb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">
              Comparación Detallada de Funcionalidades
            </h3>
            <p className="text-blue-100 text-lg">
              Todas las características incluidas en cada plan
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-4">
                  <Brain className="w-6 h-6 text-blue-400" />
                  <h4 className="text-xl font-bold text-white">Inteligencia Artificial</h4>
                </div>
                <ul className="space-y-2 text-blue-100">
                  <li>• Búsqueda conversacional con IA</li>
                  <li>• Análisis predictivo de rendimiento</li>
                  <li>• Recomendaciones inteligentes</li>
                  <li>• Procesamiento de lenguaje natural</li>
                </ul>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-4">
                  <Video className="w-6 h-6 text-blue-400" />
                  <h4 className="text-xl font-bold text-white">Análisis de Video</h4>
                </div>
                <ul className="space-y-2 text-blue-100">
                  <li>• Detección automática de jugadores</li>
                  <li>• Métricas físicas en tiempo real</li>
                  <li>• Análisis de movimiento con YOLO</li>
                  <li>• Visualizaciones interactivas</li>
                </ul>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-4">
                  <Shield className="w-6 h-6 text-blue-400" />
                  <h4 className="text-xl font-bold text-white">Soporte y Seguridad</h4>
                </div>
                <ul className="space-y-2 text-blue-100">
                  <li>• Encriptación de datos</li>
                  <li>• Backups automáticos</li>
                  <li>• Soporte técnico especializado</li>
                  <li>• Actualizaciones incluidas</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
